import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { crmApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { usePagination, dataTablePageProps } from '@/hooks/usePagination';
import type { CrmLeadResponse } from '@/types/crm';
import { showApiError } from '@/utils/apiError';

export function SocialCrmLeadsPage() {
  const pagination = usePagination();
  const [leads, setLeads] = useState<CrmLeadResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await crmApi.listLeads({ page: pagination.page, size: pagination.size });
      const social = (page.content ?? []).filter((l) =>
        (l.leadSource ?? '').toUpperCase().includes('SOCIAL'),
      );
      setLeads(social);
      pagination.applyClientCount(social.length);
    } catch (err) {
      showApiError(err, 'Failed to load CRM leads');
    } finally {
      setLoading(false);
    }
  }, [pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<CrmLeadResponse>[] = [
    { key: 'id', header: '#' },
    { key: 'leadSource', header: 'Source' },
    { key: 'leadStatus', header: 'Status' },
    { key: 'leadScore', header: 'Score' },
    {
      key: 'customer',
      header: 'Customer',
      render: (r) => r.customerName ?? '—',
    },
    {
      key: 'actions',
      header: '',
      render: () => (
        <Link to={`/crm/leads`} className="text-link text-sm">
          Open in CRM
        </Link>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="CRM Leads from Social"
        description="Leads created from inbox, comments, reviews, and intake automation"
        breadcrumbs={[{ label: 'Social', to: '/social' }, { label: 'CRM Leads' }]}
        actions={
          <Link to="/crm/leads">
            <span className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400">
              Full CRM →
            </span>
          </Link>
        }
      />
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
        Use Convert to lead on any message, comment, or review. Attribution uses lead source{' '}
        <code className="text-xs">SOCIAL_&#123;platform&#125;_&#123;type&#125;</code>.
      </p>
      <DataTable
        columns={columns}
        data={leads}
        keyField="id"
        loading={loading}
        emptyMessage="No social-sourced leads yet."
        {...dataTablePageProps(pagination)}
      />
    </div>
  );
}
